<template class="mr-3">
  <div class="card">
    <div class="card-body">
      <div>
        <a-alert
          :description="$t('caisse.alerteGestionTransactions')"
          type="info"
          show-icon
          closable
          class="mb-3"
        />
        <div style="margin-bottom: 16px; text-align: right">
          <a-button type="primary" icon="plus" @click="showAddModal = true">
            {{ $t("caisse.ajouterCaisse") }}
          </a-button>
        </div>
        <a-table
          @change="tableChanged"
          rowKey="_id"
          :pagination="true"
          :data-source="caisses"
          :columns="columns"
          :loading="tableLoading"
          :scroll="{ x: 'max-content' }"
        >
          <template slot="availability" slot-scope="text">
            <a-tag :color="text === 'OPEN' ? 'green' : 'red'">
              {{
                text === "OPEN"
                  ? $t("caisse.ouverte").toUpperCase()
                  : $t("caisse.fermee").toUpperCase()
              }}
            </a-tag>
          </template>
          <template slot="type" slot-scope="text">
            <a-tag
              :color="
                text === 'PRINCIPAL'
                  ? 'geekblue'
                  : text === 'BANK'
                  ? 'green'
                  : 'orange'
              "
            >
              {{ $t(`caisse.${text}`) }}
            </a-tag>
          </template>
          <template slot="status" slot-scope="text">
            <a-tag :color="text === 'active' ? 'green' : 'red'">
              {{
                text === "active"
                  ? $t("caisse.actif").toUpperCase()
                  : $t("caisse.inactif").toUpperCase()
              }}
            </a-tag>
          </template>
          <template slot="actions" slot-scope="text, record">
            <div class="action-icons-row">
              <a-tooltip :title="$t('caisse.settings')">
                <a-button
                  type="primary"
                  shape="circle"
                  icon="setting"
                  @click="goToCaisseProfile(record)"
                />
              </a-tooltip>
            </div>
          </template>
        </a-table>
      </div>
    </div>

    <a-modal
      v-model="showAddModal"
      :title="$t('caisse.ajouterCaisse')"
      :okText="$t('all.okay')"
      :cancelText="$t('all.cancel')"
      @ok="handleAddCaisse"
      :confirmLoading="addLoading"
      @cancel="resetAddForm"
    >
      <a-form :form="addForm">
        <a-form-item
          :label="$t('caisse.nom')"
          :labelCol="{ span: 6 }"
          :wrapperCol="{ span: 16 }"
        >
          <a-input
            v-decorator="[
              'name',
              { rules: [{ required: true, message: $t('caisse.nomRequis') }] },
            ]"
          />
        </a-form-item>
        <a-form-item
          :label="$t('caisse.reference')"
          :labelCol="{ span: 6 }"
          :wrapperCol="{ span: 16 }"
        >
          <a-input
            v-decorator="[
              'reference',
              {
                rules: [
                  { required: true, message: $t('caisse.referenceRequise') },
                ],
              },
            ]"
          />
        </a-form-item>
        <a-form-item
          :label="$t('caisse.type')"
          :labelCol="{ span: 6 }"
          :wrapperCol="{ span: 16 }"
        >
          <a-select
            v-decorator="[
              'type',
              { rules: [{ required: true, message: $t('caisse.typeRequis') }] },
            ]"
          >
            <a-select-option value="PRINCIPAL">{{
              $t("caisse.PRINCIPAL")
            }}</a-select-option>
            <a-select-option value="BANK">{{
              $t("caisse.BANK")
            }}</a-select-option>
            <a-select-option value="SUBCAISSE">{{
              $t("caisse.SUBCAISSE")
            }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item
          v-if="addForm.getFieldValue('type') === 'BANK'"
          :label="$t('caisse.banque')"
          :labelCol="{ span: 6 }"
          :wrapperCol="{ span: 16 }"
        >
          <BankSelect :value="selectedBank" @change="selectedBank = $event" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions, mapGetters } from "vuex";
import apiClient from "@/services/axios";
import moment from "moment";
import BankSelect from "@/components/BankSelect.vue";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  components: {
    BankSelect,
  },
  computed: {
    ...mapState(["settings"]),
    ...mapGetters("settings", ["btnAccessGetter"]),
    columns() {
      return [
        {
          title: "#",
          dataIndex: "index",
          key: "index",
          customRender: (text, record, index) => index + 1,
        },
        {
          title: this.$t("caisse.label"),
          dataIndex: "name",
          key: "name",
          customRender: (text, record) => {
            if (record.type === "PRINCIPAL") {
              return (
                <span>
                  <a-icon
                    type="star"
                    theme="filled"
                    class="text-warning mr-2"
                  />
                  {text}
                </span>
              );
            }
            return text;
          },
        },
        {
          title: this.$t("caisse.reference").toUpperCase(),
          dataIndex: "reference",
          key: "reference",
        },
        {
          title: this.$t("caisse.lePorteur").toUpperCase(),
          dataIndex: "owner",
          key: "owner",
          customRender: (text, record) => {
            // A caisse can have several owners.
            const owners = (record.authorizedUsers || []).filter((u) =>
              u.permissions.includes("OWNER")
            );
            return owners.map((u) => u.userName).join(", ");
          },
        },
        {
          title: this.$t("caisse.disponibiliteUpper"),
          dataIndex: "availability",
          key: "availability",
          scopedSlots: { customRender: "availability" },
        },
        {
          title: this.$t("caisse.typeUpper"),
          dataIndex: "type",
          key: "type",
          scopedSlots: { customRender: "type" },
        },
        {
          title: this.$t("caisse.statutUpper"),
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: this.$t("caisse.balance"),
          dataIndex: "balance",
          key: "balance",
          customRender: (text) => this.formatCurrency(text),
        },
        {
          title: this.$t("caisse.actions").toUpperCase(),
          key: "actions",
          scopedSlots: { customRender: "actions" },
          fixed: "right",
          width: 120,
        },
      ];
    },
    async csvToExport() {
      if (!this.btnAccessGetter.includes("pListeReglementCSV")) return [];

      const data = [...this.filtredTable];
      return data.map((item) => {
        let elems = [];
        if (item.fees)
          for (const key of Object.keys(item.fees)) {
            elems.push(formatFeeName(key, item.daily));
          }

        return {
          serial: item.serial,
          nomClient: item.nomClient,
          typeReglement: item.typeReglement,
          paiementSur: elems,
          receiptType: item.receiptType,
          montantHT: item.montantHT,
          montantTTC: item.montantTTC,
          datePaiement: moment(item.date).format("DD/MM/YYYY"),
        };
      });
    },
  },
  async created() {
    this.currency = getBuildingCurrency();
    await this.fetchCaisses();

    this.cacheData = this.caisses.map((item) => ({ ...item }));
    this.rowData = this.caisses.map((item) => ({ ...item }));
    this.tableLoading = false;
    this.filtredTableCache = this.filtredTable;
  },

  data() {
    return {
      loadedChequeData: null,
      dateRangeData: ["", ""],
      mode: ["date", "date"],
      fields: [
        "serial",
        "nomClient",
        "typeReglement",
        "paiementSur",
        "receiptType",
        "montantHT",
        "montantTTC",
        "datePaiement",
      ],

      rowData: [],
      selectedRecord: {},
      visibleCancelModal: false,
      cancelType: "Annulation",
      cancelNote: "",
      tableLoading: true,
      caisses: [],
      chequeVisible: false,
      activeCheques: {},
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      filtredTable: [],
      loadingAnnulation: false,
      filtredTableCache: [],
      printLoading: false,
      showAddModal: false,
      addLoading: false,
      addForm: this.$form.createForm(this),
      showSettingsModal: false,
      selectedCaisse: null,
      selectedBank: null,
      currency: null,
    };
  },
  watch: {
    'addForm.getFieldValue("type")': {
      handler(newValue) {
        if (newValue !== "BANK") {
          this.addForm.setFieldsValue({ bank: undefined });
        }
      },
    },
  },
  methods: {
    ...mapActions("settings", ["packHasBtnAccess"]),
    formatCurrency(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },
    async fetchCaisses() {
      this.tableLoading = true;
      try {
        const res = await apiClient.get("/compV3/caisse/");
        // Sort caisses to show PRINCIPAL type first
        this.caisses = res.data.sort((a, b) => {
          if (a.type === "PRINCIPAL" && b.type !== "PRINCIPAL") return -1;
          if (a.type !== "PRINCIPAL" && b.type === "PRINCIPAL") return 1;
          return 0;
        });
      } catch (e) {
        this.$message.error(this.$t("caisse.erreurChargementCaisses"));
      } finally {
        this.tableLoading = false;
      }
    },

    tableChanged(pagination, filters, sorter, extra) {
      this.filtredTable = extra.currentDataSource;
      this.filtredTableCache = extra.currentDataSource;
    },
    moment,

    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },
    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },

    async handleAddCaisse() {
      this.addForm.validateFields(async (err, values) => {
        if (!err) {
          this.addLoading = true;
          try {
            if (values.type === "BANK") {
              values.bank = this.selectedBank;
            }
            await apiClient.put("/compV3/caisse/", values);
            this.$message.success(this.$t("caisse.caisseAjouteeSucces"));
            this.showAddModal = false;
            this.addForm.resetFields();
            this.fetchCaisses();
          } catch (e) {
            let errorMsg = this.$t("caisse.erreurAjoutCaisse");
            if (e.response && e.response.data && e.response.data.errorCode) {
              errorMsg = this.$t(`errorCode.${e.response.data.errorCode}`);
            }
            this.$message.error(errorMsg);
          } finally {
            this.selectedBank = null;
            this.addLoading = false;
          }
        }
      });
    },
    resetAddForm() {
      this.addForm.resetFields();
      this.showAddModal = false;
    },
    goToCaisseProfile(record) {
      this.$router.push("/caisseProfile/" + record._id);
    },
  },
};
</script>

<style scoped>
.action-icons-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
